/**
 * cognito.ts — email/password flows against the Cognito user pool.
 *
 * Calls the public Cognito Identity Provider JSON API directly with fetch (no AWS
 * SDK in the bundle). These operations are unauthenticated by design and only need
 * the app client ID, which must be a public client (no secret) with the
 * ALLOW_USER_PASSWORD_AUTH and ALLOW_REFRESH_TOKEN_AUTH flows enabled.
 */
import { config } from "../config";

/** Tokens returned by a successful sign-in, refresh or SSO code exchange. */
export interface AuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken?: string; // not re-issued on refresh
  expiresIn: number; // seconds
}

/** A sign-in that needs another step before tokens are issued. */
export interface AuthChallenge {
  challenge: "NEW_PASSWORD_REQUIRED";
  session: string;
}

/** Cognito error with its type preserved (e.g. NotAuthorizedException). */
export class CognitoError extends Error {
  constructor(
    public readonly type: string,
    message: string,
  ) {
    super(message);
  }
}

// Shape of the AuthenticationResult block in Cognito responses
interface RawAuthResult {
  IdToken: string;
  AccessToken: string;
  RefreshToken?: string;
  ExpiresIn: number;
}

function toTokens(r: RawAuthResult): AuthTokens {
  return { idToken: r.IdToken, accessToken: r.AccessToken, refreshToken: r.RefreshToken, expiresIn: r.ExpiresIn };
}

// Friendlier wording for the errors users actually hit
const FRIENDLY: Record<string, string> = {
  NotAuthorizedException: "Incorrect email or password.",
  UserNotFoundException: "Incorrect email or password.", // don't reveal which accounts exist
  UserNotConfirmedException: "Please verify your email address first.",
  UsernameExistsException: "An account with this email already exists.",
  CodeMismatchException: "That code isn't right. Please check it and try again.",
  ExpiredCodeException: "That code has expired. Request a new one.",
  InvalidPasswordException: "Password must be at least 8 characters and include upper and lower case letters and a number.",
  LimitExceededException: "Too many attempts. Please wait a few minutes and try again.",
  TooManyRequestsException: "Too many attempts. Please wait a few minutes and try again.",
};

/** POST one Cognito API action and return its JSON body, or throw CognitoError. */
async function call<T>(action: string, body: Record<string, unknown>): Promise<T> {
  if (!config.cognito.clientId) {
    throw new CognitoError("NotConfigured", "Sign-in isn't set up for this environment yet.");
  }
  const res = await fetch(`https://cognito-idp.${config.cognito.region}.amazonaws.com/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": `AWSCognitoIdentityProviderService.${action}`,
    },
    body: JSON.stringify({ ClientId: config.cognito.clientId, ...body }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    // Error type arrives as e.g. "NotAuthorizedException" or "...#NotAuthorizedException"
    const type = String(json.__type ?? "UnknownError").split("#").pop()!;
    throw new CognitoError(type, FRIENDLY[type] ?? json.message ?? "Something went wrong. Please try again.");
  }
  return json as T;
}

/** Sign in with email + password. Returns tokens, or a challenge to complete. */
export async function signIn(email: string, password: string): Promise<AuthTokens | AuthChallenge> {
  const res = await call<{ AuthenticationResult?: RawAuthResult; ChallengeName?: string; Session?: string }>(
    "InitiateAuth",
    { AuthFlow: "USER_PASSWORD_AUTH", AuthParameters: { USERNAME: email, PASSWORD: password } },
  );
  if (res.AuthenticationResult) return toTokens(res.AuthenticationResult);
  if (res.ChallengeName === "NEW_PASSWORD_REQUIRED" && res.Session) {
    return { challenge: "NEW_PASSWORD_REQUIRED", session: res.Session };
  }
  throw new CognitoError("UnsupportedChallenge", `Unsupported sign-in step: ${res.ChallengeName}`);
}

/** Finish an invited user's first sign-in by replacing their temporary password. */
export async function completeNewPassword(email: string, session: string, newPassword: string): Promise<AuthTokens> {
  const res = await call<{ AuthenticationResult: RawAuthResult }>("RespondToAuthChallenge", {
    ChallengeName: "NEW_PASSWORD_REQUIRED",
    Session: session,
    ChallengeResponses: { USERNAME: email, NEW_PASSWORD: newPassword },
  });
  return toTokens(res.AuthenticationResult);
}

/** Create an account; Cognito emails a 6-digit verification code. */
export async function signUp(email: string, password: string): Promise<void> {
  await call("SignUp", {
    Username: email,
    Password: password,
    UserAttributes: [{ Name: "email", Value: email }],
  });
}

/** Verify a new account with the emailed code. */
export async function confirmSignUp(email: string, code: string): Promise<void> {
  await call("ConfirmSignUp", { Username: email, ConfirmationCode: code });
}

/** Email a password-reset code. */
export async function forgotPassword(email: string): Promise<void> {
  await call("ForgotPassword", { Username: email });
}

/** Set a new password using the emailed reset code. */
export async function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
  await call("ConfirmForgotPassword", { Username: email, ConfirmationCode: code, Password: newPassword });
}

/** Swap a refresh token for fresh ID/access tokens. */
export async function refreshSession(refreshToken: string): Promise<AuthTokens> {
  const res = await call<{ AuthenticationResult: RawAuthResult }>("InitiateAuth", {
    AuthFlow: "REFRESH_TOKEN_AUTH",
    AuthParameters: { REFRESH_TOKEN: refreshToken },
  });
  return toTokens(res.AuthenticationResult);
}

/** Revoke a refresh token (and the tokens it issued) so it can never be used again. */
export async function revokeRefreshToken(refreshToken: string): Promise<void> {
  await call("RevokeToken", { Token: refreshToken });
}
